import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { Spinner } from '@/components/app/misc';
import { Textarea } from '@/components/ui/textarea';
import { loadScript, loadStyle } from '@/lib/utils';
// Mesmas URLs da tela antiga (resources/views/backend/manage/admin/messages/form.blade.php).
const CKEDITOR_JS = 'https://cdn.ckeditor.com/ckeditor5/43.2.0/ckeditor5.umd.js';
const CKEDITOR_CSS = 'https://cdn.ckeditor.com/ckeditor5/43.2.0/ckeditor5.css';
/** Mesma configuração de plugins e barra de ferramentas da tela antiga. */
function buildConfig(CK) {
    const { Bold, Essentials, Italic, Link, Paragraph, Heading, List, BlockQuote, Font, FontSize } = CK;
    const plugins = [Essentials, Paragraph, Bold, Italic, Link].filter(Boolean);
    const toolbar = [];
    const config = {};
    if (Heading) {
        plugins.push(Heading);
        toolbar.push('heading', '|');
        config.heading = {
            options: [
                { model: 'paragraph', title: 'Parágrafo', class: 'ck-heading_paragraph' },
                { model: 'heading1', view: 'h1', title: 'Título 1', class: 'ck-heading_heading1' },
                { model: 'heading2', view: 'h2', title: 'Título 2', class: 'ck-heading_heading2' },
                { model: 'heading3', view: 'h3', title: 'Título 3', class: 'ck-heading_heading3' },
            ],
        };
    }
    toolbar.push('bold', 'italic', 'link');
    if (FontSize) {
        plugins.push(FontSize);
        toolbar.push('|', 'fontSize');
        config.fontSize = { options: [10, 12, 14, 'default', 18, 20, 22], supportAllValues: true };
    }
    if (Font) {
        plugins.push(Font);
        if (!FontSize)
            toolbar.push('|');
        toolbar.push('fontFamily', 'fontColor', 'fontBackgroundColor');
    }
    if (List) {
        plugins.push(List);
        toolbar.push('|', 'bulletedList', 'numberedList');
    }
    if (BlockQuote) {
        plugins.push(BlockQuote);
        toolbar.push('blockQuote');
    }
    toolbar.push('|', 'undo', 'redo');
    return { plugins, toolbar, ...config };
}
const editorStyles = `
.admin-rich-editor .ck-powered-by { display: none; }
.admin-rich-editor .ck.ck-editor__main > .ck-editor__editable { min-height: 200px; padding-bottom: 40px; background: hsl(var(--background)); color: hsl(var(--foreground)); }
.admin-rich-editor .ck.ck-toolbar { background: hsl(var(--muted)); border-color: hsl(var(--input)); }
.admin-rich-editor .ck.ck-editor__main > .ck-editor__editable:not(.ck-focused) { border-color: hsl(var(--input)); }
.admin-rich-editor .ck-content { line-height: 1.6; word-break: break-word; }
.admin-rich-editor .ck-content h1 { font-size: 2em; line-height: 1.2; margin: 0 0 .5rem; }
.admin-rich-editor .ck-content h2 { font-size: 1.6em; line-height: 1.25; margin: 0 0 .5rem; }
.admin-rich-editor .ck-content h3 { font-size: 1.3em; line-height: 1.3; margin: 0 0 .5rem; }
.admin-rich-editor .ck-content p { margin: 0 0 .5rem; }
.admin-rich-editor .ck-content ul { list-style: disc; padding-left: 1.25em; }
.admin-rich-editor .ck-content ol { list-style: decimal; padding-left: 1.25em; }
.dark .admin-rich-editor .ck { --ck-color-text: hsl(var(--foreground)); --ck-color-base-background: hsl(var(--background)); --ck-color-toolbar-background: hsl(var(--muted)); --ck-color-button-default-hover-background: hsl(var(--accent)); --ck-color-button-on-background: hsl(var(--accent)); --ck-color-button-on-hover-background: hsl(var(--accent)); --ck-color-dropdown-panel-background: hsl(var(--popover)); --ck-color-panel-background: hsl(var(--popover)); --ck-color-list-background: hsl(var(--popover)); --ck-color-list-button-hover-background: hsl(var(--accent)); --ck-color-input-background: hsl(var(--background)); --ck-color-input-text: hsl(var(--foreground)); --ck-color-labeled-field-label-background: hsl(var(--popover)); --ck-color-base-border: hsl(var(--input)); --ck-color-toolbar-border: hsl(var(--input)); --ck-color-panel-border: hsl(var(--border)); --ck-color-dropdown-panel-border: hsl(var(--border)); }
`;
/**
 * Editor CKEditor 5 (carregado do CDN, como na tela antiga). Se o CDN
 * falhar, vira um campo de texto simples com o HTML.
 */
export function RichEditor({ id, value, onChange, label }) {
    const holder = React.useRef(null);
    const editorRef = React.useRef(null);
    const onChangeRef = React.useRef(onChange);
    const lastValue = React.useRef(value);
    const [state, setState] = React.useState('loading');
    onChangeRef.current = onChange;
    React.useEffect(() => {
        let cancelled = false;
        loadStyle(CKEDITOR_CSS);
        loadScript(CKEDITOR_JS)
            .then(() => {
            const CK = window.CKEDITOR;
            if (cancelled || !holder.current || !CK || !CK.ClassicEditor)
                throw new Error('CKEditor indisponível');
            const element = document.createElement('div');
            holder.current.innerHTML = '';
            holder.current.appendChild(element);
            return CK.ClassicEditor.create(element, { ...buildConfig(CK), initialData: lastValue.current || '' });
        })
            .then((editor) => {
            var _a;
            if (cancelled) {
                editor.destroy();
                return;
            }
            editorRef.current = editor;
            const editable = (_a = holder.current) === null || _a === void 0 ? void 0 : _a.querySelector('.ck-editor__editable');
            if (editable)
                editable.setAttribute('aria-label', label);
            editor.model.document.on('change:data', () => {
                const data = editor.getData();
                lastValue.current = data;
                onChangeRef.current(data);
            });
            setState('ready');
        })
            .catch(() => {
            if (!cancelled)
                setState('failed');
        });
        return () => {
            cancelled = true;
            if (editorRef.current) {
                editorRef.current.destroy().catch(() => undefined);
                editorRef.current = null;
            }
        };
    }, []);
    // Valor trocado por fora (ex.: "Gerar Traduções"): atualiza o editor.
    React.useEffect(() => {
        if (value === lastValue.current)
            return;
        lastValue.current = value;
        if (editorRef.current && editorRef.current.getData() !== value)
            editorRef.current.setData(value || '');
    }, [value]);
    return (_jsxs("div", { className: "admin-rich-editor", children: [_jsx("style", { children: editorStyles }), state === 'failed' ? (_jsx(Textarea, { id: id, rows: 8, value: value, onChange: (e) => onChange(e.target.value), "aria-label": label })) : (_jsxs(_Fragment, { children: [state === 'loading' ? (_jsxs("div", { className: "flex h-[240px] items-center justify-center gap-2 rounded-md border text-sm text-muted-foreground", children: [_jsx(Spinner, {}), " Carregando editor..."] })) : null, _jsx("div", { id: id, ref: holder, className: state === 'loading' ? 'hidden' : undefined })] }))] }));
}
